/* eslint-disable react/no-unstable-nested-components */
import React, {
  useContext, useMemo, useState, useRef,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StoreContext } from 'models/import';
import Tooltip, { TooltipAlignEnum, TooltipCursorEnum, TooltipModifier } from 'components/tooltip';
import { MaterialReactTable, useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
} from 'material-react-table';
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { getLaminasApiData } from 'services/api';
import BalanceStatusTooltip, { BalanceStatusEnum } from '../../pages/overview/balanceStatusTooltip';
import { BalanceTableProps, getFormattedNumber, LocationHistoryMismatch } from './balanceTable';
import { TableDataRes } from './base/tableBaseApi';
import PlatformCard, { PlatformCardModifier } from 'components/platformCard';
import { getImportTableBalanceStatus } from './importTable';
import { getStripedRowsForTable } from 'services/util';

export interface OverviewDetailsTableDef {
  coinTicker: string;
  coinName: string;
  coinLogoUri: string | null;
  liveBalanceSpot: number;
  liveBalanceFiat: number;
  importedBalanceSpot: number;
  importedBalanceFiat: number;
  differenceSpot: number;
  differenceFiat: number;
  balanceStatus: number;
}

function OverviewDetailsTable(props: BalanceTableProps) {
  const tableRef = useRef();
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { name } = useParams();
  const { userSettings, importTableSettings, setImportTableSettings } = useContext(StoreContext);
  const [currentMismatch, setCurrentMismatch] = useState<boolean | undefined>(undefined);
  const [userCurrencyMismatch, setUserCurrencyMismatch] = useState<boolean | undefined>(undefined);

  const onDataLoaded = () => {
    // reset table settings apart from mismatches once data was loaded
    setImportTableSettings(undefined);
  };

  const onNavigate = (url: string | null) => {
    if (url && url.length) {
      window.location.href = url;
    }
  };

  const {
    data, isError, isFetching, isLoading, refetch,
  } = useQuery<TableDataRes<OverviewDetailsTableDef, any>>({
    queryKey: [{
      column: columnFilters, // refetch when columnFilters changes
      gFilter: props.globalSearch, // refetch when globalFilter changes
      index: pagination.pageIndex, // refetch when pagination.pageIndex changes
      size: pagination.pageSize, // refetch when pagination.pageSize changes
      sort: sorting, // refetch when sorting changes
      filter: props.filters, // additional external filters
      userId: userSettings?.userId,
    },
    ],
    queryFn: async (item) => {
      if (props.filters === '' || props.filters === '{}') { return null; }

      const fixedName = name === '* no value *' ? '' : encodeURIComponent(name);//backend doesn't support no value so we need to change it to empty string
      const requestUri = `/tx-overview-v2/platform-balance?customName=${fixedName || ''}`;
      const connector = requestUri.includes('?') ? '&' : '?';

      // const sortParameters = sort
      //   ? `&sort[field]=${sort.sortingField}&sort[direction]=${sort.sortingDirection}`
      //   : '';
      const sortParameters = '';
      // const sortParameters = sorting
      //   ? `&sort[field]=${sorting.sortingField}&sort[direction]=${sorting.sortingDirection}`
      //   : '';
      // this allows only 1 sort, if we want multiple we need to send all
      for (const singleSort of sorting) {
        sortParameters = `&sort[field]=${singleSort.id}&sort[direction]=${singleSort.desc}`;
      }

      const filterParameters = props.filters ? `&${props.filters}` : '';

      const response = await getLaminasApiData<TableDataRes<OverviewDetailsTableDef, any>>(
        `${requestUri}${connector}page=${pagination.pageIndex}&limit=${pagination.pageSize}${sortParameters}${filterParameters}`,
      );
      return response;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const hasLiveBalance = (row: OverviewDetailsTableDef): boolean => {
    return row.differenceSpot !== null
  };

  const columns = useMemo<MRT_ColumnDef<OverviewDetailsTableDef>[]>(() => [
    {
      accessorKey: 'coinTicker',
      Cell: (info) => {
        const coinTicker: string = info.cell.getValue() as string | '';
        const coinName = info.row.original.coinName as string | '';
        const { coinLogoUri } = info.row.original;

        const dropdownText = (coinName && coinName.length > 0) ? coinName : coinTicker;

        return (
        <Tooltip align={TooltipAlignEnum.LEFT} message={dropdownText} modifiers={[TooltipModifier.OVERVIEW]}>
          <PlatformCard
            logo_uri={coinLogoUri ? '/img/coins/' + coinLogoUri : '/img/no_coin_icon.png'}
            name={coinTicker}
            display_name={coinTicker}
            beta={false}
            modifiers={[PlatformCardModifier.OLD, PlatformCardModifier.OVERVIEW, PlatformCardModifier.SMALL_HEIGHT]}
          />
       </Tooltip>
        );
      },
      header: t('Custom Name', { ns: 'overview' }),
    },
    {
      accessorKey: 'liveBalanceSpot',
      header: '',
      Header: ({ column }) => (
        <Tooltip
        cursor={TooltipCursorEnum.HELP}
          align={TooltipAlignEnum.LEFT}
          message={
            t('These balances are not derived from imported data, but are directly reported by the respective platform.',{ ns: 'overview' })
          }
          modifiers={[]}
          key={column.id}
        >
          {t('Live Balance', { ns: 'overview' })}
        </Tooltip>
      ),
      Cell: (info) => {
        const value : number = info.cell.getValue();

        if (!value && hasLiveBalance(info.row.original)) {
          return (<span>0</span>);
        }

        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'liveBalanceFiat',
      Cell: (info) => {
        const value : number = info.cell.getValue();

        if (!value && hasLiveBalance(info.row.original)) {
          return (<span>0</span>);
        }

        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
              userSettings.currency,
            )}
          </div>
        );
      },
      header: `${t('in', { ns: 'overview' })} ${userSettings.currency}`,
    },
    {
      accessorKey: 'importedBalanceSpot',
      header: '',
      Header: ({ column }) => (
        <Tooltip
        cursor={TooltipCursorEnum.HELP}
          align={TooltipAlignEnum.LEFT}
          message={
            t('These balances are calculated based on the transactions imported from the exchange or blockchain.',{ ns: 'overview' })
          }
          modifiers={[]}
          key={column.id}
        >
          {t('Imported Balance', { ns: 'overview' })}
        </Tooltip>
      ),
      Cell: (info) => {
        const value : number = info.cell.getValue();

        if (!value && hasLiveBalance(info.row.original)) {
          return (<span>0</span>);
        }

        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'importedBalanceFiat',
      Cell: (info) => {
        const value : number = info.cell.getValue();

        if (!value && hasLiveBalance(info.row.original)) {
          return (<span>0</span>);
        }

        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
              userSettings.currency,
            )}
          </div>
        );
      },
      header: `${t('in', { ns: 'overview' })} ${userSettings.currency}`,
    },
    {
      accessorKey: 'differenceSpot',
      Cell: (info) => {
        var value : number = info.cell.getValue();

        // if (value === 0 && hasLiveBalance(info.row.original)) {
        //   return (<span>0</span>);
        // }

        if (value === null && hasLiveBalance(info.row.original)) {
          const importSpot : number = info.cell.row.original.importedBalanceSpot || 0;
          const liveSpot : number = info.cell.row.original.liveBalanceSpot || 0;
          value = Math.abs(importSpot - liveSpot)
        }

        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
            )}
          </div>
        );
      },
      header: t('Difference', { ns: 'overview' }),
    },
    {
      accessorKey: 'differenceFiat',
      Cell: (info) => {
        var value : number = info.cell.getValue();

        // if (value === 0 && hasLiveBalance(info.row.original)) {
        //   return (<span>0</span>);
        // }

        if (value === null && hasLiveBalance(info.row.original)) {
          const importSpot : number = info.cell.row.original.importedBalanceFiat || 0;
          const liveSpot : number = info.cell.row.original.liveBalanceFiat || 0;
          value = Math.abs(importSpot - liveSpot)
        }

        return (
          <div className="ct_tableBase-text-wrap">
            {getFormattedNumber(
              value,
              userSettings?.decimalCharacter,
              userSettings?.thousandCharacter,
              userSettings.currency,
            )}
          </div>
        );
      },
      header: `${t('in', { ns: 'overview' })} ${userSettings.currency}`,
    },
    {
      accessorKey: 'balanceStatus',
      enableSorting: false,
      Cell: (info) => {
        const value: BalanceStatusEnum | undefined = info.cell.getValue() as BalanceStatusEnum
        | undefined;
        if (!value && value !== 0) return '';

        const status = getImportTableBalanceStatus(value);
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignContent: 'flex-end', }}>
          <BalanceStatusTooltip status={status} isDetail cointainsLiveData={hasLiveBalance(info.row.original)} />
        </div>
        );
      },
      header: '',
    },
  ], [currentMismatch, userCurrencyMismatch]);

  return (
    <>
      <MaterialReactTable
        tableInstanceRef={tableRef}
        columns={columns}
        data={data?.data ?? []}
        initialState={{
          showColumnFilters: false,
          density: 'compact',
          columnVisibility: {
            'mrt-row-expand': false,
          },
        }}
        manualFiltering
        manualPagination
        manualSorting
        maxLeafRowFilterDepth={0}// only filter root rows
        editDisplayMode="modal"
        // enableEditing
         // Do not allow selection of fake rows
        enableRowSelection={false}
         // disable auto selection of subrows on top row selection.
         // You can still select subrows 1 by 1.
        enableSubRowSelection={false}
        enableTopToolbar={false}
        enableColumnPinning={false}
        enableRowPinning={false}
        enableExpanding
        enableColumnActions={false}
        enableColumnFilters={false}
        enableSorting={false}
        enableColumnFilterModes
        enableFacetedValues={false}
        enableExpandAll
        autoResetExpanded
        muiTableBodyRowProps={{ hover: true }}
        muiTableHeadCellProps={({column}) => ({
          sx: {
            padding: '4px',
          },
        })}
        muiTableBodyCellProps={({cell}) => ({
          sx: {
            padding: '4px',
          },
        })}
        muiTableBodyProps={({ row }) => ({ 
          sx: getStripedRowsForTable()
        })}
        muiToolbarAlertBannerProps={
          isError
            ? {
              color: 'error',
              children: t('Error loading data', { ns: 'validation' }),
            }
            : undefined
        }
        onColumnFiltersChange={setColumnFilters}
        onGlobalFilterChange={setGlobalFilter}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        // Hide default filter, we show it manually inside renderToolbarInternalActions
        positionGlobalFilter="none"
        // top right toolbar
        renderToolbarInternalActions={({ table }) => (
          <p />
        )}
        rowCount={data?.meta?.total_item_count ?? 0}
        defaultColumn={{
          size: 70,
          columnFilterModeOptions: [
            'fuzzy',
          ],
          enableColumnFilter: false,
          enableColumnFilterModes: false,
        }}
        state={{
          columnFilters,
          globalFilter,
          isLoading,
          pagination,
          showAlertBanner: isError,
          showProgressBars: false,
          showSkeletons: isFetching,
          sorting,
          showGlobalFilter: true,
        }}
        muiFilterTextFieldProps={{
          sx: { m: '0.5rem 0', width: '100%' },
        }}
        localization={{rowsPerPage: ''}}
      />
    </>
  );
}

const queryClient = new QueryClient();

function OverviewDetailsTableWithReactQueryProvider(props: BalanceTableProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <OverviewDetailsTable {...props} />
    </QueryClientProvider>
  );
}

export default OverviewDetailsTableWithReactQueryProvider;
